import React, { Component } from "react";
import PropTypes from "prop-types";
import { rgba } from "polished";
import styled, { ThemeProvider } from "styled-components";
import { RichText } from "prismic-dom";
import { Box, Flex } from "rebass";
import {
  Head,
  H1,
  H2,
  H3,
  H4,
  Text,
  Card,
  CardLayout,
  Article,
  Main,
  GradientHeader,
  GradientCard,
  PageHeader,
  PageSidebar,
  PageContent,
  PageSection,
  CalloutCard,
  CalloutContainer,
  Accordion,
} from "components";
import Link from "gatsby-link";
import { LazyImage } from "components";
import invertTheme from "../invertTheme";

const StaffLayout = styled(Flex)`
  align-items: stretch;

  @media (max-width: 30em) {
    flex-wrap: wrap;
    > * {
      width: 100%;
      margin-bottom: ${(props) => props.theme.space[1]}px;

      &:not(:lang(ar)) {
        margin-left: 0;
      }
      &:lang(ar) {
        margin-right: 0;
      }
    }
    > *:last-child {
      margin-bottom: ${(props) => props.theme.space[0]}px;
    }
  }
  @media (min-width: 30em) and (max-width: 74em) {
    flex-wrap: wrap;
    > * {
      width: ${(props) =>
        "calc(" + (1 / 2) * 100 + "% - " + props.theme.space[4] / 2 + "px)"};
      margin-bottom: ${(props) => props.theme.space[5]}px;

      &:not(:lang(ar)) {
        margin-left: ${(props) => props.theme.space[4]}px;
      }
      &:lang(ar) {
        margin-right: ${(props) => props.theme.space[4]}px;
      }
    }
    > *:nth-child(2n + 1) {
      &:not(:lang(ar)) {
        margin-left: 0;
      }
      &:lang(ar) {
        margin-right: 0;
      }
    }
    > *:nth-last-child(-n + 2) {
      margin-bottom: ${(props) => props.theme.space[0]}px;
    }
  }
  @media (min-width: 74em) {
    flex-wrap: wrap;
    > * {
      width: ${(props) =>
        "calc(" +
        (1 / 3) * 100 +
        "% - " +
        props.theme.space[4] / (1 / 3 + 1) +
        "px)"};
      margin-bottom: ${(props) => props.theme.space[5]}px;

      &:not(:lang(ar)) {
        margin-left: ${(props) => props.theme.space[4]}px;
      }
      &:lang(ar) {
        margin-right: ${(props) => props.theme.space[4]}px;
      }
    }
    > *:nth-child(3n + 1) {
      &:not(:lang(ar)) {
        margin-left: 0;
      }
      &:lang(ar) {
        margin-right: 0;
      }
    }
    > *:nth-last-child(-n + 3) {
      margin-bottom: ${(props) => props.theme.space[0]}px;
    }
  }
`;

const StaffPhoto = styled(Box)`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  background-image: ${(props) => "url(" + props.backgroundImage + ")"};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  &:after {
    /* content: ""; */
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    background: ${(props) =>
      props.theme.colors.violet &&
      "linear-gradient(to left, " +
        rgba(props.theme.colors.violet, 0.75) +
        ", " +
        rgba(props.theme.colors.violet, 0.75) +
        ")"};
  }
`;

const StaffMember = (props) => (
  <Box is={Link} to={"/" + props.object.lang + "/team/" + props.object.uid}>
    <StaffPhoto
      backgroundImage={
        props.object.data.header_image.url
          ? props.object.data.header_image.url
          : ""
      }
      alt={
        "Headshot of " +
        (props.object.data.title[0] ? props.object.data.title[0].text : "")
      }
      mb={2}
    />
    <H3
      children={
        props.object.data.title[0] ? props.object.data.title[0].text : ""
      }
      mb={1}
    />
    <H4
      children={
        props.object.data.subtitle[0] ? props.object.data.subtitle[0].text : ""
      }
    />
  </Box>
);

const BoardMember = (props) => (
  <Box>
    <H3 children={props.object.name} mb={1} />
    <H4 children={props.object.position} />
  </Box>
);

let linkResolver = function (doc) {
  return "/" + doc.type;
};

class Team extends React.Component {
  static contextTypes = {
    setAltLanguages: PropTypes.func,
  };
  componentDidMount() {
    this.context.setAltLanguages(undefined);
  }

  render() {
    let json = "";
    if (this.props.data.team.raw) {
      json = JSON.parse(this.props.data.team.raw);
    }
    return (
      <Box
        style={
          this.props.transition ? this.props.transition.style : { opacity: 0 }
        }
      >
        <Head source={json.data} />
        <Main>
          <PageHeader title={json.data.title[0].text} color={json.uid}>
            {json.data.subtitle[0] && (
              <H2 children={json.data.subtitle[0].text} fontSize={[4, 5]} />
            )}
            {json.data.description[0] && (
              <Text children={json.data.description[0].text} />
            )}
          </PageHeader>
          <PageSection>
            {json.data.body1[0] && (
              <PageSidebar>
                {json.data.body1.map((callout, index) => (
                  <CalloutCard
                    key={"callout" + index}
                    callout={callout.primary}
                    lang={json.lang}
                  />
                ))}
              </PageSidebar>
            )}
            <PageContent>
              {/* {json.data.body && (
                <Article
                  dangerouslySetInnerHTML={{
                    __html: RichText.asHtml(json.data.body, linkResolver)
                  }}
                />
              )} */}
              <Box mb={6}>
                <H2
                  children={
                    json.lang === "en-us"
                      ? "Staff Directory"
                      : json.lang === "es-mx"
                      ? "Directorio del equipo"
                      : "دليل فريق المركز"
                  }
                />
              </Box>
              {this.props.data.staff && (
                <Box>
                  <StaffLayout>
                    {this.props.data.staff.edges
                      .sort(function (a, b) {
                        if (a.node.data) {
                          if (
                            a.node.data.title[0].text.split(" ")[1] <
                            b.node.data.title[0].text.split(" ")[1]
                          )
                            return -1;
                          if (
                            a.node.data.title[0].text.split(" ")[1] >
                            b.node.data.title[0].text.split(" ")[1]
                          )
                            return 1;
                        }
                        return 0;
                      })
                      .map((member, index) => (
                        <StaffMember
                          key={"staffMember" + index}
                          object={member.node}
                        />
                      ))}
                  </StaffLayout>
                </Box>
              )}
            </PageContent>
          </PageSection>
          <ThemeProvider theme={invertTheme}>
            <PageSection style={{ backgroundColor: "#7673c0" }}>
              <PageContent>
                <Box mb={[5, 6]}>
                  {json.data.board_header[0] && (
                    <H2 children={json.data.board_header[0].text} />
                  )}
                  {json.data.board_description && (
                    <Article
                      dangerouslySetInnerHTML={{
                        __html: RichText.asHtml(
                          json.data.board_description,
                          linkResolver
                        ),
                      }}
                    />
                  )}
                </Box>
                <Box>
                  <StaffLayout>
                    {json.data.board.map((boardMember, index) => (
                      <BoardMember
                        key={"boardMember" + index}
                        object={boardMember}
                      />
                    ))}
                  </StaffLayout>
                </Box>
              </PageContent>
            </PageSection>
          </ThemeProvider>
          {/* <PageSection>
            <PageContent>
              <Box>
                {json.data.accordions.map((accordion, index) => (
                  <Accordion
                    key={"Accordion" + index}
                    title={
                      accordion.accordion_title[0]
                        ? accordion.accordion_title[0].text
                        : ""
                    }
                    data={[]}
                  >
                    {accordion.accordion_content && (
                      <Article
                        dangerouslySetInnerHTML={{
                          __html: RichText.asHtml(
                            accordion.accordion_content,
                            linkResolver
                          ),
                        }}
                      />
                    )}
                  </Accordion>
                ))}
              </Box>
            </PageContent>
          </PageSection> */}
        </Main>
      </Box>
    );
  }
}

export default Team;

export const query = graphql`
  query TeamQuery($id: String!, $lang: String!) {
    team: prismicDocument(prismicId: { eq: $id }) {
      uid
      id
      raw
      lang
    }
    staff: allPrismicDocument(
      filter: { type: { eq: "member" }, lang: { eq: $lang } }
    ) {
      edges {
        node {
          uid
          id
          lang
          data {
            title {
              text
            }
            subtitle {
              text
            }
            header_image {
              url
            }
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/layouts/team.js